var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
// External dependencies
import { useState, useEffect, useContext } from 'react';
import { Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Grid, TablePagination, Checkbox, Button, Dialog, DialogTitle, DialogContent, DialogActions, } from '@mui/material';
import { Add, ArrowDropDown, Error } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
// Internal dependencies
import { ButtonSG, ChatIconSG, DeleteIconSG, } from '../components';
import { axiosClient } from '../axios';
import { getAuthHeader } from '../auth';
import { UserContext } from '../auth';
import { DEFAULT_TEMPLATE, DEFAULT_EDITOR, MULTILANGUAGE, CLIENT_ENDPOINTS, DOCUMENT_TYPES, LOG_LEVELS, SERVICES, ENDPOINTS, DEFAULT_CHAT, } from '../constants';
import { appStyles } from '../App.styles';
import { documentsStyles } from './Documents.styles';
/**
 * Create a Document
 * @param navigate
 * @param authHeader
 * @param document
 * @param pathName
 */
export const createDocument = (user, navigate, authHeader, document, pathName) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b, _c, _d;
    try {
        // Create document
        const res = yield axiosClient.post(`${process.env.SERVER_URL}${ENDPOINTS.DOCUMENT}${ENDPOINTS.CREATE_DOCUMENT}`, { document }, { headers: authHeader });
        // Extract documentId and navigate to the document detail
        const { documentId } = (_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.data;
        navigate(`${pathName}?documentId=${documentId}`);
    }
    catch (err) {
        axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
            level: LOG_LEVELS.ERROR,
            logMessage: `Documents, createDocument, ${err === null || err === void 0 ? void 0 : err.message}`,
            metadata: {
                env: process.env.ENV,
                service: SERVICES.DOCUMENTS,
                userId: user === null || user === void 0 ? void 0 : user.uid,
                email: user === null || user === void 0 ? void 0 : user.email,
                userAgent: navigator.userAgent,
            },
        });
        // Check if the error is from axios
        if ((_b = err === null || err === void 0 ? void 0 : err.response) === null || _b === void 0 ? void 0 : _b.status) {
            // If yes, notify user with the server error message
            toast.error((_d = (_c = err === null || err === void 0 ? void 0 : err.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.message);
            return;
        }
        ;
        toast.error('Something went wrong. We are figuring it out.');
    }
    ;
});
;
/**
 * Documents element
 * @param {DocumentsProps} props
 * @returns {JSX.Element}
 */
export const Documents = function (props) {
    const { t, i18n } = useTranslation();
    // Documents
    const [documents, setDocuments] = useState([]);
    // Use React Router navigate
    const navigate = useNavigate();
    // Get use credential
    const user = useContext(UserContext);
    // Total documents
    const [total, setTotal] = useState(0);
    // Page, limit
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(10);
    // Rerender when delete
    const [deleteCount, setDeleteCount] = useState(0);
    // Get documents when user credential, page, limit, deleteCount changed
    useEffect(() => {
        const getDocuments = () => __awaiter(this, void 0, void 0, function* () {
            var _a;
            try {
                // Get auth header
                const authHeader = yield getAuthHeader(user);
                const headers = Object.assign(Object.assign({}, authHeader), { 'Accept-Language': i18n.language });
                // Get user's documents
                const res = yield axiosClient.get(`${process.env.SERVER_URL}${ENDPOINTS.DOCUMENT}${ENDPOINTS.GET_DOCUMENTS}?page=${page}&limit=${limit}`, { headers });
                // Extract total, documents
                const { total, documents } = (_a = res === null || res === void 0 ? void 0 : res.data) === null || _a === void 0 ? void 0 : _a.data;
                // Update total, documents
                setDocuments(documents);
                setTotal(total);
            }
            catch (err) {
                axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                    level: LOG_LEVELS.ERROR,
                    logMessage: `Documents, getDocuments, ${err === null || err === void 0 ? void 0 : err.message}`,
                    metadata: {
                        env: process.env.ENV,
                        service: SERVICES.DOCUMENTS,
                        userId: user === null || user === void 0 ? void 0 : user.uid,
                        email: user === null || user === void 0 ? void 0 : user.email,
                        userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                    },
                });
            }
            ;
        });
        try {
            if (user) {
                getDocuments();
            }
        }
        catch (err) {
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `Documents, useEffect, getDocuments, ${err === null || err === void 0 ? void 0 : err.message}`,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.DOCUMENTS,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                },
            });
        }
        ;
    }, [user, page, limit, deleteCount]);
    // Delete document ids 
    const [deleteDocumentIds, setDeleteDocumentIds] = useState([]);
    // Handle delete checkbox change
    const handleCheckboxChange = (event, documentId) => {
        try {
            // Get checked
            const checked = event.target.checked;
            if (checked) {
                // Add delete item
                deleteDocumentIds.push(documentId);
            }
            else {
                // Remove delete item
                const deleteIdx = deleteDocumentIds.indexOf(documentId);
                deleteDocumentIds.splice(deleteIdx, 1);
            }
            // Update delete document ids
            setDeleteDocumentIds([...deleteDocumentIds]);
        }
        catch (err) {
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `Documents, handleCheckboxChange, ${err.message}`,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.DOCUMENTS,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                },
            });
            toast.error(t('Something went wrong. We are figuring it out.'));
        }
        ;
    };
    /**
     * Handle Delete confirm click
     */
    const handleDeleteConfirmClick = () => __awaiter(this, void 0, void 0, function* () {
        var _a, _b, _c;
        try {
            // Get auth header
            const authHeader = yield getAuthHeader(user);
            const headers = Object.assign(Object.assign({}, authHeader), { 'Accept-Language': i18n.language });
            // Delete documents
            yield axiosClient.post(`${process.env.SERVER_URL}${ENDPOINTS.DOCUMENT}${ENDPOINTS.DELETE_DOCUMENTS}`, { documentIds: deleteDocumentIds }, { headers });
            // Update delete count to rerender after deleted
            setDeleteCount(deleteCount + 1);
            // Close the delete confirm dialog
            handleDeleteClickClose();
        }
        catch (err) {
            axiosClient.post(`${process.env.LOGGER_SERVER_URL}${ENDPOINTS.LOGGER}${ENDPOINTS.LOG}`, {
                level: LOG_LEVELS.ERROR,
                logMessage: `Documents, handleDeleteConfirmClick, ${err.message}`,
                metadata: {
                    env: process.env.ENV,
                    service: SERVICES.DOCUMENTS,
                    userId: user === null || user === void 0 ? void 0 : user.uid,
                    email: user === null || user === void 0 ? void 0 : user.email,
                    userAgent: navigator === null || navigator === void 0 ? void 0 : navigator.userAgent,
                },
            });
            // Check if the error is from axios
            if ((_a = err === null || err === void 0 ? void 0 : err.response) === null || _a === void 0 ? void 0 : _a.status) {
                // If yes, notify user with the server error message
                toast.error((_c = (_b = err === null || err === void 0 ? void 0 : err.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.message);
                return;
            }
            ;
            toast.error(t('Something went wrong. We are figuring it out.'));
        }
        ;
    });
    // Delete dialog open/close
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    /**
     * Handle Delete click open
     */
    const handleDeleteClickOpen = () => {
        setDeleteDialogOpen(true);
    };
    /**
     * Handle Delete click close
     */
    const handleDeleteClickClose = () => {
        setDeleteDialogOpen(false);
    };
    /**
     * Open the document
     */
    const openDocument = (document) => {
        navigate(`${CLIENT_ENDPOINTS.CHAT}?documentId=${document._id}`);
    };
    /**
     * Handle page change
     * @param {any} event
     * @param {number} value
     */
    const handlePageChange = (event, value) => {
        setPage(value);
    };
    /**
     * Handle limit change
     * @param {any} event
     */
    const handleLimitChange = (event) => {
        setLimit(event.target.value);
    };
    let dateFormat = 'DD MMM YYYY';
    switch (i18n.language) {
        case MULTILANGUAGE.en.value:
            dateFormat = 'DD MMM YYYY';
            break;
        case MULTILANGUAGE.ja.value:
            dateFormat = 'YYYY年M月D日';
            break;
        case MULTILANGUAGE.ko.value:
            dateFormat = 'YYYY년 M월 D일';
            break;
        case MULTILANGUAGE['zh-CN'].value:
            dateFormat = 'YYYY年M月D日';
            break;
        case MULTILANGUAGE['zh-HK'].value:
            dateFormat = 'YYYY年M月D日';
            break;
        case MULTILANGUAGE.id.value:
            dateFormat = 'DD MMMM YYYY';
            break;
        case MULTILANGUAGE.th.value:
            dateFormat = 'DD MMMM YYYY';
            break;
        case MULTILANGUAGE.vi.value:
            dateFormat = 'DD MMMM YYYY';
            break;
    }
    ;
    return (_jsxs(Paper
    // Project container
    , Object.assign({ 
        // Project container
        sx: {
            background: '#FFFFFF',
            boxShadow: '0px 0px 8px rgba(0, 0, 0, 0.05)',
            borderRadius: '1rem',
            height: 'calc(100%)',
            boxSizing: 'border-box',
            overflow: 'hidden',
            padding: {
                sm: '1.5rem',
                xs: '0.5rem',
            },
        } }, { children: [_jsx(Box, Object.assign({ sx: {
                    backgroundColor: '#FFFFFF',
                    padding: '0',
                    zIndex: '2',
                } }, { children: _jsxs(Grid
                // Project flex container
                , Object.assign({ 
                    // Project flex container
                    container: true, columnSpacing: 1, sx: { padding: '0 0 1.5rem', alignItems: 'center' } }, { children: [_jsx(Grid
                        // Project title flex item
                        , Object.assign({ 
                            // Project title flex item
                            item: true, xs: true, sx: { display: 'flex', width: '1px', overflow: 'hidden' } }, { children: _jsx(Typography, Object.assign({ sx: Object.assign(Object.assign({}, appStyles.text28), { textOverflow: 'ellipsis', overflow: 'hidden', width: 'calc(100%)', whiteSpace: 'nowrap' }), component: "h1" }, { children: t('Project') })) })), _jsx(Grid
                        // Project buttons flex item
                        , Object.assign({ 
                            // Project buttons flex item
                            item: true }, { children: _jsx(ButtonSG
                            // New button
                            , Object.assign({ 
                                // New button
                                variant: "contained", onClick: () => __awaiter(this, void 0, void 0, function* () {
                                    // Initialize document
                                    const document = {
                                        chat: Object.assign({}, DEFAULT_CHAT),
                                        editor: Object.assign({}, DEFAULT_EDITOR),
                                        template: Object.assign({}, DEFAULT_TEMPLATE),
                                    };
                                    // Get auth header
                                    const authHeader = yield getAuthHeader(user);
                                    // Path name to navigate to
                                    const pathName = CLIENT_ENDPOINTS.CHAT;
                                    createDocument(user, navigate, authHeader, document, pathName);
                                }), startIcon: _jsx(Add, {}) }, { children: t('New') })) })), _jsx(Grid, Object.assign({ item: true }, { children: _jsx(IconButton
                            // Delete button
                            , Object.assign({ 
                                // Delete button
                                disabled: deleteDocumentIds.length > 0 ? false : true, sx: {
                                    opacity: deleteDocumentIds.length > 0 ? '1' : '0.5',
                                    color: 'blackDark.main',
                                }, onClick: handleDeleteClickOpen }, { children: _jsx(DeleteIconSG, {}) })) }))] })) })), _jsxs(Box, Object.assign({ sx: {
                    width: 'auto',
                    height: 'calc(100% - 40px - 24px)',
                    overflow: 'auto',
                    padding: '0 0 1px',
                } }, { children: [_jsx(Box, { children: _jsx(TableContainer
                        // Documents table container
                        , { children: _jsxs(Table
                            // Documents table
                            , Object.assign({ "aria-label": "table" }, { children: [_jsx(TableHead, { children: _jsxs(TableRow, { children: [_jsx(TableCell, Object.assign({ sx: Object.assign(Object.assign({}, documentsStyles.cell), { padding: '0.875rem 1rem', width: '66%' }) }, { children: t('NAME') })), _jsx(TableCell, Object.assign({ sx: {
                                                        alignItems: 'center',
                                                        padding: '0.875rem 1rem',
                                                    } }, { children: _jsxs(Box, Object.assign({ sx: { display: 'flex', alignItems: 'center' } }, { children: [_jsx(Typography, Object.assign({ sx: Object.assign({}, documentsStyles.cell) }, { children: t('MODIFIED') })), _jsx(ArrowDropDown, { sx: { color: '#191C1A' } })] })) }))] }) }), _jsx(TableBody, { children: documents.map((document) => {
                                            var _a, _b, _c, _d, _e, _f, _g, _h;
                                            // Document data
                                            let documentTitle = DEFAULT_CHAT.title;
                                            let documentType = DOCUMENT_TYPES.CHAT;
                                            let DocumentIcon = ChatIconSG;
                                            let documentIconAlt = 'Chat icon';
                                            let documentSubtitle = undefined;
                                            if ((_a = document === null || document === void 0 ? void 0 : document.chat) === null || _a === void 0 ? void 0 : _a.title) {
                                                documentTitle = (_b = document === null || document === void 0 ? void 0 : document.chat) === null || _b === void 0 ? void 0 : _b.title;
                                                const messages = ((_c = document === null || document === void 0 ? void 0 : document.chat) === null || _c === void 0 ? void 0 : _c.messages) || [];
                                                if (messages.length > 0) {
                                                    const latestContentObj = (_e = (_d = document === null || document === void 0 ? void 0 : document.chat) === null || _d === void 0 ? void 0 : _d.messages[0]) === null || _e === void 0 ? void 0 : _e.content;
                                                    const latestContent = typeof (latestContentObj) === 'string' ? latestContentObj : (_f = latestContentObj[0]) === null || _f === void 0 ? void 0 : _f.text;
                                                    documentSubtitle = latestContent;
                                                }
                                            }
                                            else if ((_g = document === null || document === void 0 ? void 0 : document.editor) === null || _g === void 0 ? void 0 : _g.title) {
                                                documentTitle = (_h = document === null || document === void 0 ? void 0 : document.editor) === null || _h === void 0 ? void 0 : _h.title;
                                            }
                                            ;
                                            return (_jsxs(TableRow, Object.assign({ hover: true, sx: { cursor: 'pointer' }, onClick: () => { openDocument(document); } }, { children: [_jsx(TableCell, Object.assign({ sx: { padding: '0 0 0 1rem' } }, { children: _jsxs(Grid, Object.assign({ container: true, sx: { alignItems: 'center', flexWrap: 'nowrap' }, columnSpacing: 1 }, { children: [_jsx(Grid, Object.assign({ item: true }, { children: _jsx(Checkbox, { sx: {
                                                                            padding: '0',
                                                                            color: '#191C1A',
                                                                        }, onClick: (event) => {
                                                                            event.stopPropagation();
                                                                            handleCheckboxChange(event, document._id);
                                                                        } }) })), _jsxs(Grid, Object.assign({ item: true, xs: true, sx: {
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        flexWrap: 'nowrap',
                                                                    } }, { children: [_jsx(DocumentIcon, { styles: { margin: '0 0.5rem 0 0', color: 'blackDark.main' } }), _jsxs(Grid, Object.assign({ container: true, sx: { flexWrap: 'nowrap' } }, { children: [_jsx(Grid, Object.assign({ item: true, sx: { display: 'flex', alignItems: 'center' } }, { children: _jsx(Typography, Object.assign({ sx: Object.assign(Object.assign({}, appStyles.text14), { textAlign: 'left', color: '#000000', whiteSpace: 'nowrap' }) }, { children: documentTitle })) })), _jsx(Grid, Object.assign({ item: true, xs: 12, sm: true, sx: {
                                                                                        maxWidth: 'calc(100%)',
                                                                                        width: '1px',
                                                                                        overflow: 'hidden',
                                                                                    } }, { children: documentSubtitle
                                                                                        ? (_jsxs(Typography, Object.assign({ sx: Object.assign(Object.assign({}, appStyles.text14), { textAlign: 'left', fontStyle: 'italic', color: '#3F4945', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }) }, { children: ["\u00A0", `- "${documentSubtitle}"`] })))
                                                                                        : '' }))] }))] }))] })) })), _jsx(TableCell, Object.assign({ sx: Object.assign(Object.assign({}, documentsStyles.cell), { padding: '0.875rem 1rem' }) }, { children: moment.utc(document === null || document === void 0 ? void 0 : document.updatedAt).local().locale(i18n.language).format(dateFormat) }))] }), document._id));
                                        }) })] })) }) }), _jsx(Box
                    // Pagination block
                    , Object.assign({ 
                        // Pagination block
                        sx: {
                            width: '100%',
                            textAlign: 'right',
                            justifyContent: 'flex-end',
                        } }, { children: _jsx(TablePagination, { component: "div", count: total, page: page, onPageChange: handlePageChange, rowsPerPage: limit, onRowsPerPageChange: handleLimitChange, labelRowsPerPage: t('Results'), labelDisplayedRows: ({ from, to, count }) => (`${from}–${to} ${t('of')} ${count !== -1 ? count : `more than ${to}`}`) }) }))] })), _jsxs(Dialog
            // Delete dialog
            , Object.assign({ 
                // Delete dialog
                sx: {
                    '& .MuiDialog-paper': {
                        background: 'linear-gradient(0deg, rgba(0, 108, 73, 0.11), rgba(0, 108, 73, 0.11)), #FBFDF9',
                        borderRadius: '1.75rem',
                    },
                }, open: deleteDialogOpen, onClose: handleDeleteClickClose }, { children: [_jsxs(DialogTitle, Object.assign({ sx: {
                            padding: '1.5rem 1.5rem 1rem',
                        } }, { children: [_jsx(Box, Object.assign({ sx: {
                                    width: '100%',
                                    textAlign: 'center',
                                    padding: '0 0 1rem',
                                    lineHeight: '0',
                                } }, { children: _jsx(Error, { sx: { color: '#191C1A' } }) })), _jsx(Typography, Object.assign({ sx: {
                                    width: '100%',
                                    fontWeight: '400',
                                    fontSize: '1.375rem',
                                    lineHeight: '1.75rem',
                                    color: '#404943',
                                    textAlign: 'center',
                                } }, { children: t('Confirm to Delete') }))] })), _jsx(DialogContent, Object.assign({ sx: Object.assign(Object.assign({}, appStyles.text14), { color: '#404943', letterSpacing: '0.25px', padding: '0 1.5rem 1.5rem' }) }, { children: t('Please be sure — it cannot be recovered!') })), _jsxs(DialogActions, Object.assign({ sx: {
                            padding: '0 1.5rem 1.5rem',
                        } }, { children: [_jsx(Button, Object.assign({ variant: "text", sx: documentsStyles.textButton, onClick: handleDeleteClickClose }, { children: t('Cancel') })), _jsx(Button, Object.assign({ variant: "text", sx: documentsStyles.textButton, onClick: handleDeleteConfirmClick }, { children: t('Delete') }))] }))] }))] })));
};
